import type { ReactNode } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { TimeseriesPoint } from "@busright/data-contract";

import { COLORS } from "../tokens";

export interface TrendChartProps {
  readonly data: readonly TimeseriesPoint[];
  // Series color — defaults to brand accent. Pass a RAG color only for a health series.
  readonly color?: string;
  readonly height?: number;
}

const DEFAULT_HEIGHT = 220;

// Thin, typed Recharts wrapper for a single time series on a dark surface. Takes the contract's
// TimeseriesPoint directly so the FE never re-shapes data to chart it.
export const TrendChart = ({
  data,
  color,
  height = DEFAULT_HEIGHT,
}: TrendChartProps): ReactNode => (
  <ResponsiveContainer width="100%" height={height}>
    <LineChart data={data as TimeseriesPoint[]} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
      <CartesianGrid stroke={COLORS.border} strokeDasharray="3 3" vertical={false} />
      <XAxis
        dataKey="date"
        stroke={COLORS.textMuted}
        tick={{ fill: COLORS.textMuted, fontSize: 11 }}
      />
      <YAxis stroke={COLORS.textMuted} tick={{ fill: COLORS.textMuted, fontSize: 11 }} width={40} />
      <Tooltip
        contentStyle={{
          background: COLORS.surface2,
          border: `1px solid ${COLORS.border}`,
          borderRadius: 8,
          color: COLORS.textPrimary,
        }}
      />
      <Line
        type="monotone"
        dataKey="value"
        stroke={color ?? COLORS.accent}
        strokeWidth={2}
        dot={false}
      />
    </LineChart>
  </ResponsiveContainer>
);
